<template>
  <v-row>
    <slot></slot>
    <form-modal
      v-model="openModal"
      :cols="modalCols"
      :subtitle="modalSubtitle"
      title="Duplicar quantos meses a frente?"
      :value.sync="dataModal"
      @save="createMoreDepre"
    />
    <v-col :cols="12">
      <v-btn
        class="btn-plus-depre"
        data-testid="btn-nova-depreciacao"
        depressed
        @click="createDepre()"
      >
        <v-icon>mdi-plus</v-icon>
        Nova Depreciação
      </v-btn>
    </v-col>
    <v-col :cols="12" class="py-0">
      <v-alert
        data-testid="alerta-limite-competencias"
        dense
        style="font-size: 13px"
        text
        :type="excedeuLimite ? 'error' : 'info'"
      >
        {{ mensagemLimite }}
      </v-alert>
    </v-col>
    <v-col v-if="!items" :cols="12">
      <span>Nenhuma depreciação cadastrada neste item.</span>
    </v-col>
    <expansion-panel
      v-else
      :contentProps="{ eager: true }"
      :headerProps="{ color: '#9abfe6' }"
      :items="items"
      :panelProps="panelProps"
    >
      <template v-slot:header="{ item, index }">
        <span>{{ getDepreciationTitle(item) }}</span>
        <span v-if="errors.has(index)" class="has-erros">
          {{ getErrors(index) }}
        </span>
        <span
          v-if="indicesDuplicados.has(index)"
          class="has-erros"
          data-testid="aviso-depreciacao-duplicada"
        >
          Depreciação duplicada: esta competência já tem uma depreciação para este projeto
        </span>
        <v-btn
          class="icon-btn-title"
          data-testid="btn-duplicar-depreciacao"
          depressed
          icon
          :title="tituloBotaoDuplicar"
          @click.stop="openDepreModal(item)"
        >
          <v-icon>mdi-content-duplicate</v-icon>
        </v-btn>
        <template v-if="canDelete">
          <v-btn
            v-if="item.delete"
            class="icon-btn-title"
            depressed
            icon
            title="Desfazer exclusão"
            @click.stop="deleteDepre(item, true)"
          >
            <v-icon>mdi-undo-variant</v-icon>
          </v-btn>
          <v-btn
            v-else
            class="icon-btn-title"
            depressed
            icon
            title="Excluir depreciação"
            @click.stop="deleteDepre(item)"
          >
            <v-icon>mdi-trash-can</v-icon>
          </v-btn>
        </template>
      </template>
      <template v-slot:content="{ item, index }">
        <v-col :cols="12">
          <depreciation-form
            :projetos="getProjects(item.competencia)"
            :value="item"
            @competencia="competenciaHandler($event)"
            @input="inputHandler(index, $event)"
            @update:valid="setValids(index, $event)"
          ></depreciation-form>
        </v-col>
      </template>
    </expansion-panel>
  </v-row>
</template>

<script>
import { cloneDeep } from "lodash";
import moment from "moment";
import toMonth from "@/helpers/toMonth";

// A regra de limite atua no nível do mês (YYYY-MM), e não da linha: uma mesma
// competência pode ter mais de um projeto e continua contando como um único mês
// do período de utilização do item.
const toCompetenciaMes = (competencia = "") => String(competencia || "").substring(0, 7);

export default {
  components: {
    "depreciation-form": () => import("./depreciation.form.vue"),
    "expansion-panel": () => import("@/components/expansion-panel.vue"),
    "form-modal": () => import("@/components/form-modal.vue"),
  },
  computed: {
    // Meses de depreciação do item: é o teto de competências distintas.
    limiteCompetencias: function () {
      const periodo = parseInt(this.periodoDepreciacao, 10);
      return periodo > 0 ? periodo : 0;
    },
    competenciasUsadas: function () {
      const items = Array.isArray(this.items) ? this.items : [];

      return items.reduce((acc, depre) => {
        const mes = depre.delete ? "" : toCompetenciaMes(depre.competencia);

        if (mes) {
          acc.add(mes);
        }

        return acc;
      }, new Set());
    },
    // Índices das linhas que repetem competência + projeto — a identidade do
    // lançamento. Todas as ocorrências do par são marcadas (não só a segunda),
    // para o usuário ver as duas linhas em conflito. A justificativa não entra na
    // chave: mudar o texto não torna o lançamento um outro lançamento.
    indicesDuplicados: function () {
      const items = Array.isArray(this.items) ? this.items : [];
      const porChave = new Map();

      items.forEach((depre, index) => {
        const mes = depre.delete ? "" : toCompetenciaMes(depre.competencia);

        if (!mes || !depre.projetoId) {
          return;
        }

        const chave = `${mes}|${depre.projetoId}`;
        porChave.set(chave, [...(porChave.get(chave) || []), index]);
      });

      return Array.from(porChave.values()).reduce((acc, indices) => {
        if (indices.length > 1) {
          indices.forEach((index) => acc.add(index));
        }

        return acc;
      }, new Set());
    },
    excedeuLimite: function () {
      return this.limiteCompetencias > 0 && this.competenciasUsadas.size > this.limiteCompetencias;
    },
    mesesRestantes: function () {
      return Math.max(this.limiteCompetencias - this.competenciasUsadas.size, 0);
    },
    mensagemLimite: function () {
      const usadas = this.competenciasUsadas.size;

      if (this.limiteCompetencias === 0) {
        return 'Informe o "Período de Utilização (em meses)" do item: ele limita a quantidade de competências de depreciação.';
      }

      const base = `${usadas} de ${this.limiteCompetencias} competências (período de utilização do item).`;

      if (this.excedeuLimite) {
        return `${base} Remova as competências excedentes para salvar.`;
      }

      if (this.mesesRestantes === 0) {
        return `${base} Limite atingido — só é possível incluir outro projeto numa competência já cadastrada.`;
      }

      return `${base} Ainda é possível cadastrar ${this.mesesRestantes} competência(s).`;
    },
    modalCols: function () {
      return [
        {
          key: "qtd",
          name: "Quantidade",
          type: this.$fieldTypes.NUMBER,
          rules: [
            { rule: "min", params: { size: 1 }},
            { rule: "max", params: { size: this.mesesRestantes }},
          ],
        },
      ];
    },
    tituloBotaoDuplicar: function () {
      if (this.mesesRestantes === 0) {
        return 'Limite de competências do item atingido';
      }

      return `Duplicar depreciação para as próximas competências (até ${this.mesesRestantes})`;
    },
    modalSubtitle: function () {
      return `A duplicação cria uma competência nova por mês, então está limitada ao período de utilização do item: ${this.mesesRestantes} mês(es) restante(s) de ${this.limiteCompetencias}.`;
    },
    panelProps: function () {
      return (item) => ({
        disabled: item.delete,
      });
    },
  },
  created: function () {
    this.$emit('update:valid', this.generateUpdateValid());
  },
  data: function () {
    return {
      openModal: false,
      dataModal: { qtd: 1 },
      selectedItem: {},
      errors: new Map(),
      valids: new Map(),
    };
  },
  methods: {
    competenciaHandler: function (competencia = '') {
      const ano = competencia.substring(0, 4);
      const projeto = this.projectsByYear[ano];

      if (ano.length < 4) {
        return;
      }

      if (!Array.isArray(projeto) || projeto.length === 0) {
        this.$emit('loadProjectsFromYear', ano);
      }
    },
    createDepre: function () {
      const mesAtual = moment().format("YYYY-MM");
      let competencia = mesAtual;

      // Sem período de utilização não há limite conhecido: a linha é criada e o
      // aviso do painel (mais a validação do campo) cobram o preenchimento.
      if (this.limiteCompetencias > 0 && this.mesesRestantes === 0 && !this.competenciasUsadas.has(mesAtual)) {
        // Limite de competências atingido: a linha nova só pode ser mais um
        // projeto dentro de uma competência que já existe.
        const ultima = Array.from(this.competenciasUsadas).sort().pop();
        competencia = ultima;
        this.notificaErro(
          `O item permite ${this.limiteCompetencias} competência(s), todas já cadastradas. `
          + `A nova depreciação foi criada em ${toMonth(competencia)} — altere o projeto ou remova outra competência.`
        );
      }

      const depre = {
        projetoId: null,
        competencia,
        justificativa: "",
      };
      this.items.push(depre);
      this.$emit('input', this.items);
      this.competenciaHandler(depre.competencia);
    },
    createMoreDepre: function ({ qtd }, closeFn) {
      const depre = {
        projetoId: null,
        competencia: moment().format("YYYY-MM"),
        justificativa: "",
        ...this.selectedItem,
      };
      let currComp = moment(depre.competencia);
      currComp = currComp.isValid() ? currComp : moment();
      const competenciaFormatada = currComp.format("MM/YYYY");
      const nextDepres = [];
      const competencias = new Set(this.competenciasUsadas);
      const solicitado = parseInt(qtd, 10) || 0;
      let truncado = false;

      for (let i = 0; i < solicitado; i++) {
        const nextDepre = cloneDeep(depre);
        const nextComp = currComp.add(1, "month");
        const mes = nextComp.format("YYYY-MM");

        // Cada competência nova consome um mês do período de utilização do item.
        // Uma competência que já existe não consome (ela só ganha outro projeto).
        if (!competencias.has(mes) && competencias.size >= this.limiteCompetencias) {
          truncado = true;
          break;
        }

        competencias.add(mes);
        nextDepre.competencia = mes;
        nextDepre.justificativa = `Duplicado da competência ${competenciaFormatada}.\n${depre.justificativa}`;
        delete nextDepre.id;
        // a cópia é uma depreciação nova, nunca herda a marcação de exclusão da origem
        delete nextDepre.delete;
        this.competenciaHandler(nextDepre.competencia);
        nextDepres.push(nextDepre);
      }

      if (truncado) {
        this.notificaErro(
          `A duplicação parou no limite do item: ${this.limiteCompetencias} competência(s), `
          + `conforme o período de utilização. Foram criadas ${nextDepres.length} de ${solicitado}.`
        );
      }

      if (nextDepres.length === 0) {
        this.selectedItem = {};
        return closeFn();
      }

      let index = this.items.indexOf(this.selectedItem);
      index = index < 0 ? this.items.length - 1 : index + 1;

      this.items.splice(index, 0, ...nextDepres);
      this.selectedItem = {};
      this.$emit('input', this.items);
      closeFn();
    },
    deleteDepre: function (depreRef, restore = false) {
      const index = this.items.indexOf(depreRef);
      depreRef.delete = !restore;
      this.items.splice(index, 1, depreRef);
      this.$emit('input', this.items); // TODO será que só assim vai subir a alteração para cima??
    },
    generateUpdateValid: function () {
      const self = this;
      return () => {
        const valids = Array.from(self.valids.entries());
        // O Map é remontado a cada validação para o Vue 2 detectar a alteração e
        // repintar os cabeçalhos dos painéis com os campos pendentes.
        const errors = new Map();
        const numErrors = valids.reduce((acc, [index, fn]) => {
          if (self.items[index]?.delete || typeof fn !== 'function') {
            return acc;
          }

          const camposComErro = fn() || [];
          if (camposComErro.length > 0) {
            errors.set(index, camposComErro);
          }

          return acc + camposComErro.length;
        }, 0);
        self.errors = errors;
        // Cadastros antigos podem ter mais competências do que o período permite;
        // o excedente bloqueia o save aqui, como o backend também bloqueia.
        const excedente = self.excedeuLimite
          ? self.competenciasUsadas.size - self.limiteCompetencias
          : 0;
        // Linhas duplicadas (competência + projeto repetidos) também bloqueiam o
        // save aqui, como o backend bloqueia — senão o usuário só descobre no 422.
        return numErrors + excedente + self.indicesDuplicados.size;
      };
    },
    getErrors: function (index) {
      // Cópia: o cabeçalho é re-renderizado várias vezes e o pop() abaixo
      // consumiria a lista guardada no Map.
      const errors = [ ...this.errors.get(index) ];
      const lastError = errors.pop();

      if (errors.length === 0) {
        return `Há um erro no campo ${lastError}`;
      }

      return `Há erros nos campos ${errors.join(', ')} e ${lastError}`;
    },
    getDepreciationTitle: function (depreciation = {}) {
      const { competencia, projetoId } = depreciation;
      const projetos = this.getProjects(competencia);
      const projeto = projetos.find(({ id }) => projetoId === id);
      const title = projeto?.titulo || 'Título não encontrado';
      const month = competencia ? toMonth(competencia) : 'Sem competência';
      return `${title} — ${month}`;
    },
    getProjects: function (competencia = '') {
      const ano = competencia.substring(0, 4);

      if (ano.length < 4) {
        return [];
      }

      return this.projectsByYear[ano] || [];
    },
    inputHandler: function (index, depre) {
      const items = cloneDeep(this.items);
      items.splice(index, 1, depre);
      this.$emit('input', items);
    },
    notificaErro: function (mensagem) {
      this.$notify({
        title: "Atenção",
        group: 'geral',
        message: mensagem,
        type: "error",
        duration: 5000,
      });
    },
    openDepreModal: function (item) {
      if (this.limiteCompetencias === 0) {
        return this.notificaErro(
          'Informe o "Período de Utilização (em meses)" do item antes de duplicar depreciações.'
        );
      }

      // Duplicar sempre gera competências futuras: sem mês disponível não há o que fazer.
      if (this.mesesRestantes === 0) {
        return this.notificaErro(
          `Não é possível duplicar: o item já tem as ${this.limiteCompetencias} competência(s) `
          + 'permitidas pelo seu período de utilização.'
        );
      }

      this.selectedItem = item;
      this.dataModal = { qtd: 1 };
      this.openModal = true;
    },
    setValids: function (index, valid) {
      this.valids.set(index, valid);
    },
  },
  model: {
    event: 'input',
    prop: 'items',
  },
  name: 'depreciation-panels',
  props: {
    canDelete: {
      type: Boolean,
      default: false,
    },
    items: {
      type: Array,
      default: () => [],
    },
    // Período de utilização do item, em meses: limita as competências distintas
    periodoDepreciacao: {
      type: [Number, String],
      default: null,
    },
    projectsByYear: {
      type: Object,
      default: () => ({}),
    },
  },
}
</script>

<style lang="scss" scoped>
span.has-erros {
  color: red;
}

.icon-btn-title {
  &.v-btn.v-btn--depressed.v-btn--flat.v-btn--icon.v-btn--round.theme--light.v-size--default {
    flex: 0 1 36px;
    pointer-events: all;

    ::before {
      color: #fff;
    }
  } 
}

.btn-plus-depre.v-btn.v-btn--depressed.theme--light.v-size--default {
  background-color: #9abfe6;
  box-shadow: 0px 3px 1px -2px #9abfe6, 0px 2px 2px 0px #9abfe6,
    0px 1px 5px 0px #9abfe6;
  color: #fff;
  float: right;
}

  .v-expansion-panel-header {
    color: #fff;
    justify-content: space-between;
  }
  
  .theme--light.v-expansion-panels
    .v-expansion-panel-header
    .v-expansion-panel-header__icon
    .v-icon {
    color: #fff;
  }
</style>
